// @ts-strict-ignore
import {extend} from 'common/util';
import {debug} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {insertAtIndex, removeByIndex, replaceByIndex, unique} from 'common/util/array';
import {FILTER_DETERMINER_ALL, FILTER_DETERMINER_ANY} from 'common/report/insights/util/constants';
import {
  cohortEntryIsInvalidForDataGroup,
  isClauseInvalidForDataGroup,
} from 'common/query-builder/query-builder/util/data-groups';
import {XAU_ONLY_MATH_TYPES} from 'common/report/constants';
import {INSIGHTS_COHORT_TYPE} from 'common/widgets/property-filter-menu/models/insights-filter';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaShowClause} from 'common/util/formulas';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {InsightsResourceType, MetricType, PRIMITIVE_MATH_TYPES, XAU_MATH_TYPES} from 'common/types/reports/bookmark';

import {BehaviorMetric, FilterDeterminer} from '../util/types';
import {Clause} from './clause';
import {GroupClause} from './group-clause';
import {CohortsClause} from './cohorts-clause';
import {TimeClause} from './time-clause';
import {BehaviorShowClause, FormulaShowClause, ShowClause, ShowClauseClassType} from './show-clause';
import {FormulaClause} from './formula-clause';
import {FilterClause} from './filter-clause';
import {ClauseType, ClauseTypeEnum, DeserializeReturn} from './types';
import {displayOptionsAreDefault} from './utils';

type BaseClauseType<AttributesType> = {
  attrs: AttributesType;
  extend: (attrs: Partial<AttributesType>) => AttributesType | BaseClauseType<AttributesType>;
  serialize: () => any;
  toBookmarkData: () => any;
  toUrlData: (params?: any) => any;
  valid: boolean;
};

export class Section<A, C extends BaseClauseType<A>> {
  declare clauses: C[];
  declare TYPE: ClauseTypeEnum;

  constructor(attrs: any = {}) {
    const clauseArgs = (Array.isArray(attrs) ? attrs : [attrs]).filter(this.validClauseFn);

    this.clauses = [...clauseArgs, ...(attrs.clauses || [])];
  }

  get attrs() {
    const {clauses} = this;
    return {clauses};
  }

  get valid() {
    return this.clauses.every((clause) => clause.valid);
  }

  // Another less repetitive but more confusing way to do this:
  // https://stackoverflow.com/questions/68232682/return-discriminated-union-from-factory-function-in-typescript
  // Sadly both solutions are error prone in that if you return the wrong
  // section type there will be no error thrown by the compiler.
  static create(sectionType: typeof ClauseType.Cohorts, attrs): CohortsSection;
  static create(sectionType: typeof ClauseType.Filter, attrs): FilterSection;
  static create(sectionType: typeof ClauseType.Formula, attrs): FormulaSection;
  static create(sectionType: typeof ClauseType.Group, attrs): GroupSection;
  static create(sectionType: typeof ClauseType.Show, attrs): ShowSection;
  static create(sectionType: typeof ClauseType.Time, attrs): TimeSection;
  static create(sectionType: ClauseTypeEnum, attrs): any;
  static create(sectionType: ClauseTypeEnum, attrs): AllSectionTypes {
    switch (sectionType) {
      case ShowSection.TYPE:
        return new ShowSection(attrs);
      case CohortsSection.TYPE:
        return new CohortsSection(attrs);
      case GroupSection.TYPE:
        return new GroupSection(attrs);
      case FilterSection.TYPE:
        return new FilterSection(attrs);
      case FormulaSection.TYPE:
        return new FormulaSection(attrs);
      case TimeSection.TYPE:
        return new TimeSection(attrs);
    }
  }
  static deserialize(sectionType: typeof ClauseType.Cohorts, clauseAttrs): DeserializeReturn<CohortsSection>;
  static deserialize(sectionType: typeof ClauseType.Filter, clauseAttrs): DeserializeReturn<FilterSection>;
  static deserialize(sectionType: typeof ClauseType.Formula, clauseAttrs): DeserializeReturn<FormulaSection>;
  static deserialize(sectionType: typeof ClauseType.Group, clauseAttrs): DeserializeReturn<GroupSection>;
  static deserialize(sectionType: typeof ClauseType.Show, clauseAttrs): DeserializeReturn<ShowSection>;
  static deserialize(sectionType: typeof ClauseType.Time, clauseAttrs): DeserializeReturn<TimeSection>;
  static deserialize(sectionType: ClauseTypeEnum, clauseAttrs): DeserializeReturn<AllSectionTypes>;
  static deserialize<T extends Obj>(
    sectionType: ClauseTypeEnum,
    clauseAttrs: T | T[] | Section<T, BaseClauseType<T>> | {clauses: T[]},
  ): Pick<Section<T, BaseClauseType<T>>, `clauses`> {
    if (Array.isArray(clauseAttrs)) {
      clauseAttrs = {clauses: clauseAttrs};
    }

    if (clauseAttrs.clauses) {
      return Section.create(
        sectionType,
        extend(clauseAttrs, {
          clauses: clauseAttrs.clauses.map((clause) => Section.createClause(sectionType, clause)),
        }),
      );
    }
    return {clauses: []};
  }

  static createClause(sectionType: ClauseTypeEnum, clause) {
    // TODO(CC): Remove value comparison
    if (Array.isArray(clause)) {
      // if feature flag hasn't been fetched, check sectionType
      if (sectionType === ClauseType.Group) {
        return Section.create(
          sectionType,
          clause.map((c) => Clause.create(sectionType, c)),
        );
      }
      const newClause = {...clause[0], filterOperator: undefined, filterValue: undefined, filterType: undefined};
      return Section.create(sectionType, Clause.create(sectionType, newClause));
    }
    return Clause.create(sectionType, clause);
  }

  validClauseFn(clause) {
    return [ShowClause, FilterClause, TimeClause, GroupClause, CohortsClause, FormulaClause].some(
      (clauseType) => clause instanceof clauseType,
    );
  }

  create(sectionType: ClauseTypeEnum, attrs: any) {
    return Section.create(sectionType, attrs);
  }

  serialize() {
    return this.clauses.map((c) => c.serialize());
  }

  toBookmarkData() {
    return this.clauses.map((c) => c.toBookmarkData());
  }

  toUrlData() {
    return this.clauses.map((c) => c.toUrlData());
  }

  validate(newSection: this): this {
    const valid = newSection.valid;

    if (!valid) {
      debug.warn(`${this.TYPE} section invalid:`, newSection);
    }

    return valid ? newSection : this;
  }

  hasClauses() {
    return !!this.clauses.length;
  }

  getLastClause() {
    return this.hasClauses() ? this.clauses[this.clauses.length - 1] : null;
  }

  appendClause(newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: insertAtIndex(this.clauses, this.clauses.length, newClause)})),
    );
  }

  insertClause(index, newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: insertAtIndex(this.clauses, index, newClause)})),
    );
  }

  replaceClause(index, newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: replaceByIndex(this.clauses, index, newClause)})),
    );
  }

  removeClause(index) {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: removeByIndex(this.clauses, index)})));
  }

  removeEmptyClauses() {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: []})));
  }

  removeAllClauses() {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: []})));
  }

  filterClauses(filterFn): this {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: this.clauses.filter(filterFn)})),
    ) as unknown as this;
  }
}

export class ShowSection extends Section<ShowClause[`attrs`], ShowClauseClassType> {
  static TYPE = ShowClause.TYPE;
  TYPE = ShowClause.TYPE;

  get valid() {
    return true;
  }

  get hasPerEventFilters() {
    return this.clauses.some(
      (clause) => clause instanceof BehaviorShowClause && clause.behavior.filters && clause.behavior.filters.length,
    );
  }

  _getResourceTypes() {
    return unique(
      this.clauses.map((clause: ShowClauseClassType) =>
        clause instanceof FormulaShowClause ? InsightsResourceType.Formulas : clause.behavior.resourceType,
      ),
    );
  }

  hasEventsQuery() {
    const resourceTypes = this._getResourceTypes();
    return resourceTypes.some((resourceType) => resourceType === InsightsResourceType.Events);
  }

  hasFunnelsQuery() {
    return this.clauses.some(
      (clause) => clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel,
    );
  }

  hasAnyOrderFunnelsQuery() {
    return this.clauses.some(
      (clause) =>
        clause instanceof BehaviorShowClause &&
        clause.behavior.type === MetricType.Funnel &&
        clause.behavior.behaviors?.some((behavior) => behavior.funnelOrder === `any`),
    );
  }

  isPeopleOnlyQuery() {
    const resourceTypes = this._getResourceTypes();
    const hasPeopleClauses = resourceTypes.some((type) => type === InsightsResourceType.People);
    const hasOnlyFormulasAndPeople = resourceTypes.every(
      (type) => type === InsightsResourceType.People || type === InsightsResourceType.Formulas,
    );

    return hasPeopleClauses && hasOnlyFormulasAndPeople;
  }

  hasPeopleQuery() {
    const resourceTypes = this._getResourceTypes();
    return resourceTypes.some((item) => item === InsightsResourceType.People);
  }

  hasCohortsOverTime() {
    return this._getResourceTypes().includes(InsightsResourceType.Cohorts);
  }

  setDataGroupId(dataGroupId: string) {
    const clauses = this.clauses.map((clause) => {
      return clause instanceof BehaviorShowClause
        ? new BehaviorShowClause({
            behavior: {
              ...clause.behavior,
              dataGroupId,
            },
            measurement: {...clause.measurement},
          })
        : clause;
    });
    return this.validate(Section.create(this.TYPE, {...this, clauses}) as this);
  }

  makeValidForDataGroup({
    dataGroupId,
    customEvents,
    metricsResource,
    behaviorsResource,
  }: {
    dataGroupId: string;
    customEvents: any[];
    metricsResource: MetricsResource;
    behaviorsResource: BehaviorsResource;
  }): this | ShowSection {
    let newSection: this | ShowSection = this.filterClauses((clause) => {
      return !this._clauseInvalidForDataGroup({dataGroupId, customEvents, clause, metricsResource, behaviorsResource});
    });
    if (newSection.clauses.length === 0) {
      newSection = ShowSection.createShowSection();
    }
    return newSection;
  }

  _clauseInvalidForDataGroup({
    dataGroupId,
    customEvents,
    clause,
    metricsResource,
    behaviorsResource,
  }: {
    dataGroupId: string;
    customEvents: any[];
    clause: ShowClauseClassType;
    metricsResource: MetricsResource;
    behaviorsResource: BehaviorsResource;
  }) {
    if (clause instanceof FormulaShowClause) {
      return getResolvedFormulaShowClause({
        metricsResource,
        behaviorsResource,
        formulaShowClause: clause,
      })?.referencedMetrics?.some((c) =>
        isClauseInvalidForDataGroup({clause: c as BehaviorMetric, dataGroupId, customEvents}),
      );
    }
    return isClauseInvalidForDataGroup({clause: clause as BehaviorMetric, dataGroupId, customEvents});
  }

  hasXauClauses() {
    return this.getBehaviorClauses().some((clause) => clause.isXauClause());
  }

  lowestXauTimeUnit() {
    // time unit is the minimum unit of all XAU clauses, e.g. a DAU and WAU report's time unit = `day`
    const xauOperators = this.getBehaviorClauses()
      .map((clause) => clause.measurement.math)
      .filter((math) => XAU_ONLY_MATH_TYPES.includes(math as XAU_MATH_TYPES));
    if (xauOperators.includes(XAU_MATH_TYPES.DAU)) {
      return XAU_MATH_TYPES.DAU;
    } else if (xauOperators.includes(XAU_MATH_TYPES.WAU)) {
      return XAU_MATH_TYPES.WAU;
    } else if (xauOperators.includes(XAU_MATH_TYPES.MAU)) {
      return XAU_MATH_TYPES.MAU;
    }
    reporter.error(`Unhandled XAU math type in "${xauOperators}"`);

    return undefined;
  }

  hasRollingClauses(): boolean {
    return this.clauses.some((clause) => clause.isRollingClause());
  }

  hasCumulativeClauses(): boolean {
    return this.clauses.some((clause) => clause.isCumulativeClause());
  }

  hasCumulativeUniqueClauses(): boolean {
    return this.getBehaviorClauses().some((clause) => clause.isCumulativeUniqueClause());
  }

  hasCustomUnits(): boolean {
    return this.getBehaviorClauses().some((clause) => !displayOptionsAreDefault(clause.display));
  }

  getBehaviorClauses(): BehaviorShowClause[] {
    return this.clauses.filter((clause): clause is BehaviorShowClause => clause instanceof BehaviorShowClause);
  }

  /**
   * Get BehaviorShowClauses plus any that are in FormulaShowClause.formula.referencedMetrics
   * Note: this method is similar to the function getExpandedBehaviorShowClauses in
   * ../utils.ts (which operates on plain object types)
   */
  getExpandedBehaviorClauses({
    metricsResource,
    behaviorsResource,
  }: {
    metricsResource: MetricsResource;
    behaviorsResource: BehaviorsResource;
  }): BehaviorShowClause[] {
    return this.clauses
      .map((clause) => {
        if (clause instanceof BehaviorShowClause) {
          return clause;
        }
        const resolvedFormulaClause = getResolvedFormulaShowClause({
          metricsResource,
          behaviorsResource,
          formulaShowClause: clause,
        });
        return (
          resolvedFormulaClause?.referencedMetrics?.map(
            (referencedMetric) => new BehaviorShowClause(referencedMetric),
          ) || []
        );
      })
      .flat();
  }

  getFormulaClauses(): FormulaShowClause[] {
    return this.clauses.filter((clause): clause is FormulaShowClause => clause instanceof FormulaShowClause);
  }

  hasFormulaClause() {
    return this.clauses.some((clause) => clause instanceof FormulaShowClause);
  }

  static createShowSection({
    behavior = {} as BehaviorShowClause[`behavior`],
    measurement = {} as BehaviorShowClause[`measurement`],
    isExpanded = false,
  } = {}): ShowSection {
    const {
      dataset = null,
      dataGroupId = null,
      type = MetricType.Event,
      resourceType = InsightsResourceType.Events,
    } = behavior;
    const {math} = measurement;
    // when name is `null` it's for `Report#createDefaultReport`,
    // # ShowSection#constructor by default expects the old show clause, need to specific pass in `behavior` and `measurement` to force it to recognize new show clause
    // when name is `undefined` it's for old show clause with `show.value`
    if (behavior.name === null || behavior.name || behavior.id || behavior.raw_cohort) {
      return new ShowSection(
        ShowClause.createShowClause({
          behavior: {
            ...behavior,
            dataset,
            dataGroupId,
            resourceType: resourceType || InsightsResourceType.Events,
            type: type || MetricType.Event,
          },
          measurement: {
            ...measurement,
            math: math || PRIMITIVE_MATH_TYPES.TOTAL,
          },
          isExpanded,
        }),
      );
    }
    return new ShowSection();
  }
}

// Cohort Breakdowns (group bys). Only separate from "GroupSection" for historical reasons
export class CohortsSection extends Section<CohortsClause[`attrs`], CohortsClause> {
  static TYPE = CohortsClause.TYPE;
  TYPE = CohortsClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof CohortsClause);
  }

  makeValidForDataGroup(dataGroupId) {
    return this.filterClauses((clause) => !cohortEntryIsInvalidForDataGroup({item: {cohort: clause}, dataGroupId}));
  }
}

export class GroupSection extends Section<GroupClause[`attrs`], GroupClause> {
  static TYPE = GroupClause.TYPE;
  TYPE = GroupClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every(this.validClauseFn);
  }

  validClauseFn(clause) {
    // Allow nested groupsections for value comparisons
    // TODO: Remove GroupSection
    return clause instanceof GroupClause || clause instanceof GroupSection;
  }

  isPeopleTimeSeries() {
    const lastClause = this.clauses.length && this.clauses[this.clauses.length - 1];
    return lastClause.propertyType === `datetime`;
  }

  getGroupByCohortsClauses() {
    const groupByCohortsClauses = this.clauses.filter((clause) => clause.isGroupByCohorts());
    return groupByCohortsClauses.flatMap((clause) => clause.cohorts);
  }

  hasGroupByCohortsClause() {
    return this.clauses.some((clause) => clause.isGroupByCohorts());
  }

  hasGroupByPropertyClause() {
    return this.clauses.some((clause) => !clause.isGroupByCohorts() && !!clause.value);
  }

  hasUserClauses() {
    return this.clauses.some((clause) => this._isUserClause(clause));
  }

  removeUserClauses(dataGroupId) {
    if (dataGroupId) {
      return this.filterClauses((clause) => !this._isUserClause(clause));
    }
    return this;
  }

  setDataGroupIdByResourceType(dataGroupId: string, resourceType: InsightsResourceType) {
    const clauses = this.clauses.map((clauseOrSection) => {
      if (clauseOrSection instanceof GroupSection) {
        return clauseOrSection.setDataGroupIdByResourceType(dataGroupId, resourceType);
      }
      return clauseOrSection.extend(clauseOrSection.resourceType === resourceType ? {dataGroupId} : {});
    });

    return this.validate(Section.create(ClauseType.Group, extend(this, {clauses})) as this);
  }

  _isUserClause({resourceType, dataGroupId}: {resourceType?: InsightsResourceType; dataGroupId?: string}) {
    return resourceType === InsightsResourceType.People && !dataGroupId;
  }
}

export class FilterSection extends Section<FilterClause[`attrs`], FilterClause> {
  static TYPE = FilterClause.TYPE;
  TYPE = FilterClause.TYPE;
  static DETERMINER_ALL = FILTER_DETERMINER_ALL;
  static DETERMINER_ANY = FILTER_DETERMINER_ANY;
  static DETERMINER_TYPES = [FilterSection.DETERMINER_ALL, FilterSection.DETERMINER_ANY];

  declare determiner: FilterDeterminer;

  constructor(attrs: any = {}) {
    super(attrs);
    // The determiner could be saved on the attrs object OR on the first filter clause.
    const savedDeterminer = (attrs && attrs.determiner) || (this.clauses.length && this.clauses[0].determiner);
    if (savedDeterminer && FilterSection.DETERMINER_TYPES.includes(savedDeterminer)) {
      this.determiner = savedDeterminer;
    } else {
      this.determiner = FilterSection.DETERMINER_ALL;
    }

    if (this.clauses && this.clauses.length < 2) {
      // reset to all when only one clause
      this.determiner = FilterSection.DETERMINER_ALL;
    }

    if (this.clauses.length) {
      this.clauses[0].determiner = this.determiner;
    }
  }

  get valid() {
    return (
      super.valid &&
      this.clauses.every((clause) => clause instanceof FilterClause) &&
      FilterSection.DETERMINER_TYPES.includes(this.determiner)
    );
  }

  setDataGroupIdByResourceType(dataGroupId: string, resourceType: InsightsResourceType) {
    const clauses = this.clauses.map((clause) =>
      clause.extend(clause.resourceType === resourceType ? {dataGroupId} : {}),
    );
    return this.validate(Section.create(ClauseType.Filter, extend(this, {clauses})) as this);
  }

  removeMultipleCohortFilters() {
    return this.filterClauses((clause) => !(clause.value === INSIGHTS_COHORT_TYPE && clause.filterValue.length > 1));
  }

  makeValidForDataGroup(dataGroupId) {
    return this.filterClauses((clause) => !this._clauseInvalidForDataGroup({clause, dataGroupId}));
  }

  _clauseInvalidForDataGroup({clause, dataGroupId}) {
    const {resourceType, propertyFilter} = clause;
    const cohort =
      propertyFilter &&
      propertyFilter.filterValue &&
      propertyFilter.filterValue[0] &&
      propertyFilter.filterValue[0].cohort;
    return (
      (cohort && cohortEntryIsInvalidForDataGroup({item: {dataGroupId, cohort}})) ||
      (resourceType === InsightsResourceType.People && !clause.dataGroupId)
    );
  }

  serialize() {
    const clauses = super.serialize();
    if (clauses.length > 1) {
      clauses[0].determiner = this.determiner;
    }
    return clauses;
  }

  toBookmarkData() {
    const clauses = super.toBookmarkData();
    if (clauses.length > 1) {
      clauses[0].determiner = this.determiner;
    }
    return clauses;
  }

  toUrlData(): any {
    const clauses = super.toUrlData();
    return clauses.length ? {clauses, determiner: this.determiner} : {};
  }
}

export class FormulaSection extends Section<FormulaClause[`attrs`], FormulaClause> {
  static TYPE = FormulaClause.TYPE;
  TYPE = FormulaClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof FormulaClause);
  }
}

export class TimeSection extends Section<TimeClause[`attrs`], TimeClause> {
  static TYPE = TimeClause.TYPE;
  TYPE = TimeClause.TYPE;

  get valid() {
    return super.valid && this.clauses.length === 1 && this.clauses.every((clause) => clause instanceof TimeClause);
  }
}

export type AllSectionTypes =
  | CohortsSection
  | FilterSection
  | FormulaSection
  | GroupSection
  | ShowSection
  | TimeSection;
